import { engineComponent, Utils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Reel } from "../GameComponents/Reel";
import { Settings } from "./Settings";
import { Symbol } from "../GameComponents/Symbol";
import { CustomUtils } from "../Utils/CustomUtils";
import { Tweens } from "../Components/Effects/Tweens";
import { Symbols } from "../GameComponents/Symbols";

/**
 * ReelExtended handles:
 * - z-order
 * - usage of Normal instead of Rotation
 */
@engineComponent(ComponentTypes.DisplayObject)
export class ReelExtended extends Reel {
    public rollDirection: number = 1;
    protected isForceStopped: boolean = false;
    protected customData: Object = {};
    private _symbolCaseInUpdateReel = Symbol.CaseBlur;
    private overlay: Array<number>;
    private teasings: Array<Object> = [];
    protected lastSymbolId: number;

    public initialize(
        previousReel: Reel,
        nextReel: Reel,
        index: number,
        symbols: Symbols,
        strip: Array<number>,
        positionX: number,
        positionY: number,
        symbolCount: number,
        symbolTopCount: number,
        symbolBottomCount: number,
        symbolWidth: number,
        symbolHeight: number,
        startDelay: number,
        stopDelay: number,
    ): void {
        super.initialize(
            previousReel,
            nextReel,
            index,
            symbols,
            strip,
            positionX,
            positionY,
            symbolCount,
            symbolTopCount,
            symbolBottomCount,
            symbolWidth,
            symbolHeight,
            startDelay,
            stopDelay,
        );

        this.addCustomData({ originalReels: strip });
    }

    //******************************************************************************************************************
    //  SHOW SYMBOLS
    //******************************************************************************************************************

    /**
     * Naplni pole "currentSymbols" symbolmi, ktore su na screene, plus symbolmi podla poctu "symbolTopCount" a "symbolBottomCount"
     *
     * @param {boolean} useTransform Use current symbols from WinAnimator.
     */
    public showSymbols(useTransform: boolean = false): void {
        this.shouldBeStopping = false;
        this.isStopping = false;
        this.stoppingCounter = 0;
        this.isTeasing = false;
        this.teasingSymbols = [];

        this.x = this.positionX;
        this.y = this.positionY;

        if (!useTransform) {
            while (this.currentSymbols.length > 0) {
                this.returnSymbol(this.currentSymbols.pop());
            }
        }

        let rowsLenght = this.symbolBottomCount + this.symbolCount + this.symbolTopCount;

        for (let i = 0; i < rowsLenght; i++) {
            let symbolId: number;
            let symbolCase: string;

            if (i < this.symbolBottomCount) {
                symbolId =
                    this.strip[
                        (this.data.index + this.symbolBottomCount + this.symbolCount - i - 1 + this.strip.length) %
                            this.strip.length
                    ];
                symbolCase =
                    i === this.symbolBottomCount - 1
                        ? CustomUtils.getCase(symbolId, Symbol.CaseRotation)
                        : CustomUtils.getCase(symbolId, Symbol.CaseBlur);
            } else if (i < this.symbolBottomCount + this.symbolCount) {
                symbolId = this.data.symbols[this.symbolBottomCount + this.symbolCount - i - 1];
                symbolCase = CustomUtils.getCase(symbolId, Symbol.CaseRotation);

                if (CustomUtils.isRespinActive() || CustomUtils.isRespinLast()) {
                    if (
                        this.data.hasOwnProperty("overlay") &&
                        this.data.overlay[this.symbolBottomCount + this.symbolCount - i - 1] == Settings.SymbolBigXSpins
                    ) {
                        // symbol in overlay but not in transform => sticked in previous rounds
                        symbolCase = Symbol.CaseEmpty;
                    }
                }
            } else {
                symbolId =
                    this.strip[
                        (this.data.index + this.symbolBottomCount + this.symbolCount - i - 1 + this.strip.length) %
                            this.strip.length
                    ];
                symbolCase =
                    i === this.symbolBottomCount + this.symbolCount
                        ? CustomUtils.getCase(symbolId, Symbol.CaseRotation)
                        : CustomUtils.getCase(symbolId, Symbol.CaseBlur);
            }

            if (!useTransform) {
                // when we don't want use symbols from WinAnimator
                this.currentSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase));
            } else {
                // currentSymbols are used from WinAnimator. We only need to add new top and bottom symbols
                if (i === 0) {
                    this.currentSymbols.push(this.getSymbol(symbolId - 1, symbolCase));
                } else if (i === rowsLenght - 1) {
                    this.currentSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase));
                }
            }
        }

        for (let i = 0; i < this.currentSymbols.length; i++) {
            if (this.currentSymbols[i] != null) {
                const symbol = this.currentSymbols[i];
                symbol.x = -symbol.offsetX;
                symbol.y = this.symbolHeight * (i - this.symbolTopCount) - symbol.offsetY;
                this.addChild(this.currentSymbols[i]);
            }
        }
    }

    //******************************************************************************************************************
    //  SPIN START
    //******************************************************************************************************************

    public spinStart(): void {
        this.isForceStopped = false;
        this.lastSymbolId = null;

        const originalReels = this.getCustomData("originalReels");
        if (originalReels != null) {
            this.setStrip(originalReels);
        }

        super.spinStart();
    }

    /**
     *
     */
    public respinStart(): void {
        this.isForceStopped = false;

        const respinReels = this.getCustomData("respinReels");
        if (respinReels != null) {
            this.setStrip(respinReels);
        }
        super.spinStart();
    }

    //******************************************************************************************************************
    //  SPIN STOP
    //******************************************************************************************************************

    /**
     * @param {boolean} force
     * @param {number} customDelay
     */
    public spinStop(force: boolean = false, customDelay: number = 0): void {
        this.isForceStopped = force;

        if (force) {
            this.isTeasing = false;
            this.teasingSymbols = [];
        }

        super.spinStop(force, customDelay);
    }

    //******************************************************************************************************************
    //  SPIN PRE ROLL
    //******************************************************************************************************************

    protected spinPreRoll(): void {
        Tweens.removeTweens(this, "position");
        Tweens.positionTween(this, {
            tweenX: [this.positionX, this.positionX, this.positionX],
            tweenY: [this.positionY, this.positionY - this.preRoll * this.rollDirection, this.positionY],
            tweenDuration: this.preRollSpeed * 2,
            tweenDelay: this.startDelay,
            callbackFunction: this.spinRoll,
            callbackScope: this,
        });
    }

    //******************************************************************************************************************
    //  SPIN ROLL
    //******************************************************************************************************************

    protected spinRoll(): void {
        Tweens.removeTweens(this, "position");
        Tweens.positionTween(this, {
            tweenX: [this.positionX, this.positionX],
            tweenY: [this.positionY, this.positionY + this.symbolHeight * this.rollDirection],
            tweenDuration: this.rollSpeed,
            callbackFunction: this.updateReel,
            callbackScope: this,
        });
    }

    protected updateReel(): void {
        if (this.rollDirection > 0) {
            const lastSymbol = this.currentSymbols.pop();
            if (lastSymbol != null) {
                this.returnSymbol(lastSymbol);
            }
        } else {
            const firstSymbol = this.currentSymbols.shift();
            if (firstSymbol != null) {
                this.returnSymbol(firstSymbol);
            }
        }

        if (this.shouldBeStopping && !this.isStopping) {
            if (this.previousReel === null) {
                this.isStopping = true;
            } else if ((<ReelExtended>this.previousReel).isStopping) {
                this.isStopping = true;
            }
        }

        this.stripIndex = (this.stripIndex - 1 + this.strip.length) % this.strip.length;

        let symbolId = this.strip[this.stripIndex];
        let symbolCase = CustomUtils.getCase(symbolId, Symbol.CaseBlur);

        if (this.isStopping) {
            if (this.stoppingCounter < this.symbolBottomCount) {
                // Bottom over reel symbol

                symbolId =
                    this.strip[
                        (this.data.index +
                            this.symbolBottomCount +
                            this.symbolCount -
                            this.stoppingCounter -
                            1 +
                            this.strip.length) %
                            this.strip.length
                    ];
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount - 1
                        ? CustomUtils.getCase(symbolId, Symbol.CaseRotation)
                        : CustomUtils.getCase(symbolId, Symbol.CaseBlur);
            } else if (this.stoppingCounter < this.symbolBottomCount + this.symbolCount) {
                symbolId = this.data.symbols[this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1];
                symbolCase = CustomUtils.getCase(symbolId, Symbol.CaseRotation);

                if (CustomUtils.isRespinActive() || CustomUtils.isRespinLast()) {
                    if (
                        this.data.hasOwnProperty("overlay") &&
                        this.data.overlay[this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1] ==
                            Settings.SymbolBigXSpins
                    ) {
                        // symbol in overlay but not in transform => sticked in previous rounds
                        if (!this.data.hasOwnProperty("transform")) {
                            symbolCase = "ROTATION_HIDING";
                        } else if (
                            this.data.hasOwnProperty("transform") &&
                            this.data.transform[this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1] ==
                                -1
                        ) {
                            symbolCase = "ROTATION_HIDING";
                        }
                    }
                }
            } else {
                symbolId =
                    this.strip[
                        (this.data.index +
                            this.symbolBottomCount +
                            this.symbolCount -
                            this.stoppingCounter -
                            1 +
                            this.strip.length) %
                            this.strip.length
                    ];
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount + this.symbolCount
                        ? CustomUtils.getCase(symbolId, Symbol.CaseRotation)
                        : CustomUtils.getCase(symbolId, Symbol.CaseBlur);
            }

            this.stoppingCounter++;
        }

        this.currentSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase));

        this.addChild(this.currentSymbols[0]);

        for (let i = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];
            symbol.x = -symbol.offsetX;
            symbol.y = this.symbolHeight * (i - this.symbolTopCount) - symbol.offsetY;
        }

        if (
            this.isStopping &&
            this.stoppingCounter === this.symbolBottomCount + this.symbolCount + this.symbolTopCount
        ) {
            this.spinPostRoll();
        } else {
            this.spinRoll();
        }
    }

    //******************************************************************************************************************
    //  SPIN POST ROLL
    //******************************************************************************************************************

    protected spinPostRoll(): void {
        let coordsY: Array<number> = [];
        let coordsX: Array<number> = [];

        coordsY.push(this.positionY);
        coordsX.push(this.positionX);

        for (let i = 0; i < this.postRollShakes; i++) {
            let coordY = i % 2 == 1 ? -this.postRoll : this.postRoll;
            coordsY.push(this.positionY + coordY);
            coordsX.push(this.positionX);
        }

        coordsY.push(this.positionY);
        coordsX.push(this.positionX);

        Tweens.removeTweens(this, "position");
        Tweens.positionTween(this, {
            tweenX: coordsX,
            tweenY: coordsY,
            tweenDuration: this.postRollSpeed * 2,
            callbackFunction: this.spinComplete,
            callbackScope: this,
        });
    }

    //******************************************************************************************************************
    //  SPIN COMPLETE
    //******************************************************************************************************************

    protected spinComplete(): void {
        Tweens.removeTweens(this, "position");

        while (this.currentSymbols.length > 0) {
            const lastSymbol = this.currentSymbols.pop();
            if (lastSymbol != null) {
                this.returnSymbol(lastSymbol);
            }
        }

        this.emit(Reel.ReelStopped, this.index);
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    protected getSymbol(symbolId: number, symbolCase: string): Symbol {
        const symbol = super.getSymbol(symbolId, symbolCase);

        if (this.isTeasing) {
            if (this.teasingSymbols.indexOf(symbol.id) !== -1) {
                // Teased symbol
                if (symbol.getCurrentCase() === Symbol.CaseBlur) {
                    symbol.goToCase(Symbol.CaseBlur);
                }
            } else {
                // Non-teased symbol
                if (symbol.getCurrentCase() === Symbol.CaseBlur || symbol.getCurrentCase() === Symbol.CaseRotation) {
                    symbol.goToCase(Symbol.CaseNowin); //Symbol.CaseNowinBlur
                }
            }
        }

        return symbol;
    }

    protected returnSymbol(symbol: Symbol): void {
        if (!Utils.isNullOrUndefined(symbol)) {
            symbol.alpha = 1;
            this.symbols.returnSymbol(symbol);
        }
    }

    public setTease(isTeasing: boolean, teasingSymbols: Array<number> = null): void {
        this.isTeasing = isTeasing;
        this.teasingSymbols = teasingSymbols;
    }

    public addTeasing(teasings: Array<Object>): void {
        this.teasings = teasings;
    }

    public setStrip(strip: Array<number>): void {
        this.strip = strip;
    }

    public addCustomData(data: Object): void {
        this.customData[Object.keys(data)[0]] = data[Object.keys(data)[0]];
    }

    public getCustomData(name: string): any {
        if (this.customData[name] != null) {
            return this.customData[name];
        }

        return null;
    }

    public addCurrentSymbolsToReel(currentSymbols: Array<Symbol>): void {
        this.currentSymbols = [];
        for (let symbolIndex = 0; symbolIndex < this.symbolCount; symbolIndex++) {
            this.currentSymbols.push(currentSymbols[symbolIndex]);
            currentSymbols[symbolIndex] = null;
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/ReelExtended.ts
 **/